import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Filter, X, RotateCcw } from 'lucide-react'
import DateRangePicker from './DateRangePicker'
import './ReportFilter.css'

/**
 * ReportFilter Component
 *
 * A comprehensive filter bar for reports with date range, status filters,
 * and other customizable filter options.
 *
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onChange - Filter change handler
 * @param {Function} props.onApply - Apply filters handler
 * @param {Function} props.onReset - Reset filters handler
 * @param {Array} props.filterConfig - Filter configuration
 * @param {boolean} props.showDateRange - Show date range picker
 * @param {boolean} props.showApplyButton - Show apply button
 * @param {boolean} props.collapsible - Enable collapsible filter panel
 */
const ReportFilter = ({
	filters = {},
	onChange,
	onApply,
	onReset,
	filterConfig = [],
	showDateRange = true,
	showApplyButton = true,
	collapsible = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(!collapsible)

	// Count active filters
	const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
		if (key === 'dateRange') {
			return value && value.preset
		}
		return value !== '' && value !== null && value !== undefined
	}).length

	// Handle individual filter change
	const handleFilterChange = (key, value) => {
		const newFilters = { ...filters, [key]: value }
		onChange(newFilters)

		// Auto-apply if no apply button
		if (!showApplyButton && onApply) {
			onApply(newFilters)
		}
	}

	// Handle date range change
	const handleDateRangeChange = (dateRange) => {
		handleFilterChange('dateRange', dateRange)
	}

	// Handle reset
	const handleReset = () => {
		const defaultFilters = filterConfig.reduce((acc, filter) => {
			acc[filter.key] = filter.defaultValue ?? ''
			return acc
		}, {})
		defaultFilters.dateRange = { preset: 'last_30_days' }

		onChange(defaultFilters)
		if (onReset) {
			onReset()
		}
	}

	// Render filter input based on type
	const renderFilterInput = (filter) => {
		switch (filter.type) {
			case 'select':
				return (
					<select
						value={filters[filter.key] ?? ''}
						onChange={(e) => handleFilterChange(filter.key, e.target.value)}
						className="report-filter__select"
					>
						<option value="">{filter.placeholder || 'All'}</option>
						{filter.options?.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				)

			case 'text':
				return (
					<input
						type="text"
						value={filters[filter.key] ?? ''}
						onChange={(e) => handleFilterChange(filter.key, e.target.value)}
						placeholder={filter.placeholder}
						className="report-filter__input"
					/>
				)

			case 'number':
				return (
					<input
						type="number"
						value={filters[filter.key] ?? ''}
						onChange={(e) => handleFilterChange(filter.key, e.target.value)}
						placeholder={filter.placeholder}
						min={filter.min}
						max={filter.max}
						className="report-filter__input"
					/>
				)

			case 'checkbox':
				return (
					<label className="report-filter__checkbox">
						<input
							type="checkbox"
							checked={filters[filter.key] ?? false}
							onChange={(e) => handleFilterChange(filter.key, e.target.checked)}
						/>
						<span>{filter.checkboxLabel || filter.label}</span>
					</label>
				)

			default:
				return null
		}
	}

	return (
		<div className="report-filter">
			{collapsible && (
				<button className="report-filter__toggle" onClick={() => setIsExpanded(!isExpanded)}>
					<Filter size={16} />
					<span>Filters</span>
					{activeFilterCount > 0 && <span className="report-filter__badge">{activeFilterCount}</span>}
				</button>
			)}

			{isExpanded && (
				<div className="report-filter__content">
					<div className="report-filter__fields">
						{showDateRange && (
							<div className="report-filter__field">
								<DateRangePicker
									value={filters.dateRange || {}}
									onChange={handleDateRangeChange}
									label="Date Range"
								/>
							</div>
						)}

						{filterConfig.map((filter) => (
							<div key={filter.key} className="report-filter__field">
								{filter.label && filter.type !== 'checkbox' && (
									<label className="report-filter__label">{filter.label}</label>
								)}
								{renderFilterInput(filter)}
							</div>
						))}
					</div>

					<div className="report-filter__actions">
						{showApplyButton && onApply && (
							<button className="report-filter__btn report-filter__btn--primary" onClick={() => onApply(filters)}>
								Apply Filters
							</button>
						)}

						<button className="report-filter__btn report-filter__btn--secondary" onClick={handleReset}>
							<RotateCcw size={14} />
							<span>Reset</span>
						</button>

						{activeFilterCount > 0 && (
							<div className="report-filter__active-count">
								<span>{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>
								<button className="report-filter__clear" onClick={handleReset}>
									<X size={14} />
								</button>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

ReportFilter.propTypes = {
	filters: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onApply: PropTypes.func,
	onReset: PropTypes.func,
	filterConfig: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string,
			type: PropTypes.oneOf(['select', 'text', 'number', 'checkbox']).isRequired,
			placeholder: PropTypes.string,
			options: PropTypes.arrayOf(
				PropTypes.shape({
					value: PropTypes.string.isRequired,
					label: PropTypes.string.isRequired,
				})
			),
			defaultValue: PropTypes.any,
			min: PropTypes.number,
			max: PropTypes.number,
			checkboxLabel: PropTypes.string,
		})
	),
	showDateRange: PropTypes.bool,
	showApplyButton: PropTypes.bool,
	collapsible: PropTypes.bool,
}

export default ReportFilter
